import posthog from 'posthog-js';
import { PROD_URL, STAGING_URL } from './constants';
import { user_type } from './enums';

const POSTHOG_LOCAL_DEV_OVERRIDE = process.env.REACT_APP_POSTHOG_LOCAL_DEV === 'true';

const APP_NAME = 'core';
const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';

interface AuthenticatedUser {
  id: number;
  email: string;
  name?: string;
  date_joined?: string;
  profile: {
    user_type: number;
    district?: {
      id: number;
      name: string;
      tier?: string;
      state?: string;
      nces_id?: string;
    };
  };
}

let pendingUser: AuthenticatedUser | null = null;

const isValidPostHogEnvironment = () => {
  const hostname = window.location.hostname;
  const isProductionEnv = hostname === STAGING_URL || hostname === PROD_URL;
  return isProductionEnv || POSTHOG_LOCAL_DEV_OVERRIDE;
};

export const isPostHogEnabled = () => {
  return posthog.__loaded;
};

export const initPostHog = (key: string, host: string) => {
  if (!key) return;

  if (posthog.__loaded) return;

  if (!isValidPostHogEnvironment()) return;

  posthog.init(key, {
    api_host: host || DEFAULT_POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
    debug: false,
    opt_in_site_apps: true,
    loaded: (posthog) => {
      posthog.register({ app_name: APP_NAME });
      posthog.capture('$pageview', { $current_url: window.location.href });
      if (pendingUser) {
        identifyPostHogUser(pendingUser);
        pendingUser = null;
      }
    },
  });
};

export const identifyPostHogUser = (user: AuthenticatedUser) => {
  if (!user?.id) return;

  if (!isPostHogEnabled()) {
    pendingUser = user;
    return;
  }

  const userTypeLabel =
    user_type().find((ut: { key: number }) => ut.key === user.profile.user_type)?.label ||
    'Unknown';

  posthog.identify(user.id.toString(), {
    email: user.email,
    name: user?.name || '',
    signed_up_at: user?.date_joined || '',
    user_type: userTypeLabel,
    district_id: user.profile?.district?.id || null,
    district_name: user.profile?.district?.name || '',
    district_nces_id: user.profile?.district?.nces_id || null,
  });

  if (user.profile?.district) {
    posthog.group('district', user.profile.district.id.toString(), {
      name: user.profile.district.name,
      ats_tier: user.profile.district.tier || '',
      state: user.profile.district.state || '',
      nces_id: user.profile.district.nces_id || '',
    });
  }
};

export const resetPostHogUser = () => {
  pendingUser = null;
  if (!isPostHogEnabled()) return;
  posthog.reset();
  posthog.register({ app_name: APP_NAME });
};

export const capturePostHogEvent = (eventName: string, properties?: Record<string, unknown>) => {
  if (!isPostHogEnabled()) return;
  posthog.capture(eventName, properties);
};

export const trackCandidateSearch = ({
  query,
  result_count,
  duration_ms,
}: {
  query: string;
  result_count: number;
  duration_ms: number;
}) => {
  capturePostHogEvent('candidate_search_performed', {
    query,
    result_count,
    found_results: result_count > 0,
    duration_ms,
  });
};

export const capturePostHogPageView = () => {
  if (!isPostHogEnabled()) return;
  posthog.capture('$pageview', {
    $current_url: window.location.href,
  });
};

export default posthog;
